import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ssoAdminConsentRedirect } from '@/lib/auth/sso-auth';

/**
 * Public "Connect Microsoft 365" page. An organization administrator enters
 * their domain and is sent to Azure AD to grant one-time admin consent for the
 * Metaonia publisher app. No Metaonia sign-in is required to reach this page.
 */
export function ConnectPage() {
  const [searchParams] = useSearchParams();
  const [domain, setDomain] = useState(searchParams.get('org') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;

    setError(null);
    setIsLoading(true);
    try {
      await ssoAdminConsentRedirect(domain.trim());
      // On success the browser is redirected to Microsoft; nothing follows.
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      console.error('Admin consent init failed:', err);
      setError(`Unable to start setup: ${detail}`);
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/logo.png" alt="Metaonia" className="mx-auto mb-4 h-64 w-auto" />
          <CardTitle className="text-xl">Connect Microsoft 365</CardTitle>
          <CardDescription>
            Connect your organization&apos;s Microsoft Entra ID so your team can sign in to
            Metaonia with single sign-on. You&apos;ll grant consent once as a Global Administrator.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="domain">Organization domain</Label>
              <Input
                id="domain"
                type="text"
                placeholder="acme.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={isLoading || !domain.trim()}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Connect Microsoft 365
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            You must be a Global Administrator (or Privileged Role Administrator) of your
            Microsoft tenant to grant consent.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
